import {useContext, useState} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import AtomImage from '@s-ui/react-atom-image'
import Context from '@s-ui/react-context'
import {useRouter} from '@s-ui/react-router'
import Layout from '@adv-mt/mt-layout-basic'

import VehicleConfirmation from '../StepFormSellerPlate/components/VehicleConfirmation/index.js'
import VehicleSelection from '../StepFormSellerPlate/components/VehicleSelection/index.js'
import {BASE_CLASS_NAME} from '../StepFormSellerPlate/config.js'

const StepVehicleConfirmation = ({onAppraisalFound, onSelectVehicle, plateVehicleResults, userData}) => {
  const {domain} = useContext(Context)
  const router = useRouter()

  const [isLoading, setIsLoading] = useState(false)

  const config = domain.get('config')
  const urlStatics = config.get('urlStatics')
  const {PUBLISH_MY_AD_PATH_PATTERN} = config.get('page')
  const {km, plate, vehicles} = plateVehicleResults
  const isOnlyOneVehicle = vehicles.length === 1
  const areManyVehicles = vehicles.length > 1

  const contentClassName = cx(`${BASE_CLASS_NAME}-content`, {
    [`${BASE_CLASS_NAME}-content--fullWidth`]: areManyVehicles
  })

  const handleDenySelection = () => {
    router.push(`${PUBLISH_MY_AD_PATH_PATTERN}?withPlate=true&withPlateStatus=plateListNotMyCar`)
  }

  const handleSelectVehicle = async ({plateStatus, vehicleSelected = 0} = {}) => {
    setIsLoading(true)

    const {
      bodyTypeId,
      doors,
      fuelTypeId,
      make: makeName,
      makeId,
      model: modelName,
      modelId,
      plateRegistrationDate,
      plateRegistrationYear,
      transmissionTypeId,
      vehicleStatus,
      version: versionName,
      versionId
    } = vehicles[vehicleSelected]

    if (plateStatus !== 'plateEditField') {
      const [appraisalError, instantOffer] = await domain.get('get_instant_offer_seller_use_case').execute({
        bodyTypeDoors: `${bodyTypeId}-${doors}`,
        email: userData?.email,
        fuelId: fuelTypeId,
        km,
        makeId,
        makeName,
        modelId,
        modelName,
        name: userData?.name,
        phone: userData?.phone,
        plate,
        postalCode: userData?.postalCode,
        transmissionTypeId,
        vehicleStatus,
        versionId,
        versionName,
        year: plateRegistrationYear
      })

      if (!appraisalError) {
        onAppraisalFound({instantOffer, vehicleSelected})
        return
      }
    }

    const [draftError, draftResponse] = await domain.get('obtain_draft_ad_publisher_use_case').execute()

    if (draftError) {
      setIsLoading(false)
      return
    }

    const {
      data: {id: adId}
    } = draftResponse
    const fields = {
      bodyTypeDoors: `${bodyTypeId}-${doors}`,
      carPlate: plate,
      fuelId: fuelTypeId,
      km,
      makeId,
      modelId,
      plateRegistrationDate,
      publishInMA: true,
      transmissionTypeId,
      versionId,
      year: plateRegistrationYear
    }

    await domain.get('edit_draft_ad_publisher_use_case').execute({adId, fields})
    onSelectVehicle({
      adId,
      destinationUrl: `${PUBLISH_MY_AD_PATH_PATTERN}?withPlate=true&withPlateStatus=${
        plateStatus ?? 'plateRecommendedPrice'
      }&plateVehicleSelected=${vehicleSelected}`,
      fields
    })
  }

  const handleVehicleConfirmationSelect = ({plateStatus} = {}) => {
    handleSelectVehicle({plateStatus})
  }

  return (
    <Layout.Container isFullWidth>
      <div className={BASE_CLASS_NAME}>
        <main className={contentClassName}>
          {isOnlyOneVehicle && (
            <VehicleConfirmation
              isLoading={isLoading}
              onDenySelection={handleDenySelection}
              onSelectVehicle={handleVehicleConfirmationSelect}
              vehicleData={{plate, km, ...vehicles[0]}}
            />
          )}
          {areManyVehicles && (
            <VehicleSelection
              isLoading={isLoading}
              km={km}
              onDenySelection={handleDenySelection}
              onSelectVehicle={handleSelectVehicle}
              plate={plate}
              vehicles={vehicles}
            />
          )}
        </main>
        {isOnlyOneVehicle && (
          <aside className={`${BASE_CLASS_NAME}-imageWrapper`}>
            <AtomImage
              aria-hidden="true"
              role="presentation"
              src={`${urlStatics}images/illustrations/car-documentation.svg`}
            />
          </aside>
        )}
      </div>
    </Layout.Container>
  )
}

StepVehicleConfirmation.displayName = 'StepVehicleConfirmation'

StepVehicleConfirmation.propTypes = {
  onAppraisalFound: PropTypes.func.isRequired,
  onSelectVehicle: PropTypes.func.isRequired,
  plateVehicleResults: PropTypes.shape({
    km: PropTypes.number,
    plate: PropTypes.string,
    vehicles: PropTypes.array
  }).isRequired,
  userData: PropTypes.shape({
    email: PropTypes.string,
    name: PropTypes.string,
    phone: PropTypes.string,
    postalCode: PropTypes.string
  })
}

export default StepVehicleConfirmation
